import React, { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import api from "@/lib/api";
import useApiList from "@/hooks/useApiList";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Radio, ExternalLink, Loader2, UserCheck, Lock, Info } from "lucide-react";
import { Link } from "react-router-dom";

export default function LivePage() {
  useSeo({ title: "Live Sessions", description: "Upcoming live CSI sessions and webinars delivered by CSI faculty.", path: "/live" });
  const { user } = useAuth();
  const { data: sessions, loading, error, refetch, setData } = useApiList("/live-sessions");
  const [mine, setMine] = useState([]);

  useEffect(() => {
    if (user) api.get("/live-sessions/upcoming/me").then((r) => setMine(r.data)).catch(() => {});
  }, [user]);

  const toggleRsvp = async (s) => {
    try {
      if (s.my_rsvp) {
        const { data } = await api.delete(`/live-sessions/${s.id}/rsvp`);
        toast.success("RSVP cancelled");
        setData && setData((prev) => prev.map((x) => x.id === s.id ? { ...x, my_rsvp: false, rsvp_count: data.rsvp_count } : x));
        setMine((prev) => prev.map((x) => x.id === s.id ? { ...x, my_rsvp: false, rsvp_count: data.rsvp_count } : x));
      } else {
        const { data } = await api.post(`/live-sessions/${s.id}/rsvp`);
        toast.success("See you there - RSVP noted");
        setData && setData((prev) => prev.map((x) => x.id === s.id ? { ...x, my_rsvp: true, rsvp_count: data.rsvp_count } : x));
        setMine((prev) => prev.map((x) => x.id === s.id ? { ...x, my_rsvp: true, rsvp_count: data.rsvp_count } : x));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "RSVP failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="eyebrow mb-2">Live & scheduled</div>
      <h1 className="font-display text-4xl sm:text-5xl mb-6">Pick a seat, pour tea, drop in.</h1>
      <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5 flex gap-3 text-sm text-amber-900 max-w-4xl" data-testid="live-access-note">
        <Info size={18} className="shrink-0 mt-0.5"/>
        <div>
          <b>Live sessions are for registered members enrolled in the course.</b> To attend, you need a CSI account and a paid
          enrolment in the programme the session belongs to - join links and RSVP unlock automatically once your enrolment is confirmed.
          <div className="flex flex-wrap gap-3 mt-2 font-semibold">
            {!user && <Link to="/register" className="underline" data-testid="live-note-register">Create an account</Link>}
            {!user && <Link to="/login" className="underline" data-testid="live-note-login">Log in</Link>}
            <Link to="/courses" className="underline" data-testid="live-note-courses">Browse programmes &amp; enrol</Link>
          </div>
        </div>
      </div>

      {user && mine.length > 0 && (
        <section className="mb-10">
          <h2 className="font-display text-2xl mb-4">Your upcoming sessions</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {mine.map((s) => <SessionCard key={s.id} s={s} mine user={user} onToggleRsvp={toggleRsvp}/>)}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-display text-2xl mb-4">All live sessions</h2>
        {loading && sessions.length === 0 ? (
          <div className="py-16 text-center text-zinc-500 inline-flex items-center gap-2 w-full justify-center">
            <Loader2 size={16} className="animate-spin"/> Loading live sessions…
          </div>
        ) : error && sessions.length === 0 ? (
          <div className="py-16 text-center">
            <div className="text-sm text-zinc-700 mb-2">We couldn't reach the live schedule. Retrying automatically…</div>
            <button className="chip" onClick={refetch}>Retry now</button>
          </div>
        ) : sessions.length === 0 ? (
          <div className="py-16 text-center text-zinc-500">No live sessions scheduled yet.</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sessions.map((s) => <SessionCard key={s.id} s={s} user={user} onToggleRsvp={toggleRsvp}/>)}
          </div>
        )}
      </section>
    </div>
  );
}

function SessionCard({ s, mine, user, onToggleRsvp }) {
  const when = new Date(s.scheduled_at);
  const isSoon = when - new Date() < 30 * 60 * 1000;
  const canJoin = s.can_join || mine;
  return (
    <div className="card-warm p-5" data-testid={`live-session-${s.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow">{s.course_title}</span>
        {isSoon && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600"><span className="live-dot"/> Starting soon</span>}
      </div>
      <div className="font-display text-xl">{s.title}</div>
      <div className="text-sm text-zinc-600 mt-1">
        {when.toLocaleString()} · {s.duration_min}m
        {s.rsvp_count ? <> · <span className="inline-flex items-center gap-1 text-[var(--terracotta)] font-semibold"><UserCheck size={12}/> {s.rsvp_count} attending</span></> : null}
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        {canJoin && (s.meeting_url || s.zoom_url) ? (
          <a href={s.meeting_url || s.zoom_url} target="_blank" rel="noreferrer" className="btn-ink px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1" data-testid={`join-session-${s.id}`}>
            <Radio size={14}/> Join <ExternalLink size={12}/>
          </a>
        ) : !canJoin && (
          <Link to={s.course_id ? `/courses/${s.course_id}` : "/courses"} className="chip text-sm inline-flex items-center gap-1" data-testid={`live-locked-${s.id}`}>
            <Lock size={13}/> {user ? "Enrol in course to attend" : "Register & enrol to attend"}
          </Link>
        )}
        {user && canJoin && (
          <button
            onClick={() => onToggleRsvp(s)}
            className={`chip text-sm inline-flex items-center gap-1 ${s.my_rsvp ? "bg-emerald-100 text-emerald-800 border-emerald-200" : ""}`}
            data-testid={`live-rsvp-${s.id}`}
          >
            <UserCheck size={13}/> {s.my_rsvp ? "I'm attending" : "I'll attend"}
          </button>
        )}
        {mine && <span className="chip">Enrolled</span>}
      </div>
    </div>
  );
}
